// src/components/ProductManagement/ProductList.js
import React, { useState, useEffect } from "react";
import ProductTable from "./ProductTable";
import Select from "react-select";
import "./ProductList.css";
import SearchComponent from "../../../components/SearchComponent";
import { ProductService } from "../../../services/productService";
import { Modal, Spin } from "antd";
import AddProductForm from "./AddProductForm";
import { toast } from "react-toastify";
import ImportProductsForm from "./ImportProductsForm";
import ButtonComponent from "../../../components/ButtonComponent/ButtonComponent";
import { IconAdd, IconUpload } from "../../../assets/icons";
import debounce from "lodash.debounce";

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [order, setOrder] = useState("asc");
  const [totalRecord, setTotalRecord] = useState(0);
  const [columns, setColumns] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false); // Trạng thái modal
  const [isImportModalVisible, setIsImportModalVisible] = useState(false); // Trạng thái modal

  const [loading, setLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [allProductList, setAllProductList] = useState([]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await ProductService.getProducts({
        page: currentPage,
        pageSize: pageSize,
        search: search,
        category: category,
        order: order,
      });
      if (!response.Data) {
        toast.error("Lỗi lấy danh sách sản phẩm!");
        return;
      }
      const { totalRecord, columns, listData } = response.Data;
      setTotalRecord(totalRecord);
      setColumns(columns);
      setProducts(listData);
    } catch (error) {
      toast.error("Lỗi lấy danh sách sản phẩm!");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const responseData = await ProductService.getAllProducts();
      if (responseData.Data) {
        setAllProductList(responseData.Data);
      } else {
        toast.error("Lỗi lấy danh sách tất cả sản phẩm!");
        return;
      }
    } catch (error) {
      toast.error("Lỗi lấy danh sách tất cả sản phẩm!");
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, search, category, order]);

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const handleSearchChange = debounce((value) => {
    setSearch(value);
  }, 500);

  const handleCategoryChange = (selectedOption) => {
    setCategory(selectedOption.value);
  };

  const handleOrderChange = (selectedOption) => {
    setOrder(selectedOption.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(e.target.value);
  };

  const showModal = () => {
    setIsModalVisible(true); // Mở modal khi nhấn nút Thêm sản phẩm
  };

  const showImportModal = () => {
    setIsImportModalVisible(true); // Mở modal khi nhấn nút Thêm sản phẩm
  };

  const handleCancel = () => {
    setEditingProduct(null);
    setIsModalVisible(false); // Đóng modal khi nhấn Huỷ
  };

  const handleImportCancel = () => {
    setIsImportModalVisible(false); // Đóng modal khi nhấn Huỷ
  };

  const handleAddProduct = async (productData) => {
    try {
      setLoading(true);
      // Gọi API thêm sản phẩm
      const response = await ProductService.addProduct(productData);
      if (response.Data) {
        setIsModalVisible(false); // Đóng modal
        fetchProducts(); // Lấy lại danh sách sản phẩm sau khi thêm
        fetchAllProducts();
      } else {
        toast.error("Có lỗi xảy ra!");
      }
    } catch (error) {
      toast.error("Có lỗi xảy ra khi thêm sản phẩm!");
    } finally {
      setLoading(false);
    }
  };

  const handleImportProducts = async (formData) => {
    setLoading(true);
    try {
      const response = await ProductService.importProducts(formData);
      if (response.Data) {
        toast.success("Tải lên sản phẩm thành công!");
        fetchProducts(); // Cập nhật danh sách sản phẩm
        fetchAllProducts();
        handleImportCancel();
      } else {
        toast.error(response.message || "Tải lên thất bại!");
      }
    } catch (error) {
      toast.error("Có lỗi xảy ra!");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProduct = async (id, updatedProduct) => {
    try {
      setLoading(true);
      const response = await ProductService.updateProduct(id, updatedProduct);
      if (response.Data) {
        fetchProducts(); // Gọi API để làm mới danh sách
        fetchAllProducts();
      } else {
        toast.error("Có lỗi xảy ra!");
      }
    } catch (error) {
      toast.error("Có lỗi xảy ra!");
      console.error(error);
    } finally {
      setIsModalVisible(false); // Đóng modal
      setLoading(false);
    }
  };

  const handleDeleteSelectedProducts = async () => {
    if (selectedProducts.length === 0) return;
    try {
      const confirmDelete = window.confirm(
        `Bạn có chắc chắn muốn xóa ${selectedProducts.length} sản phẩm đã chọn?`
      );
      if (!confirmDelete) return;
      setLoading(true);
      const response = await ProductService.deleteSelectedProducts(
        selectedProducts
      );
      if (response.Status === 200) {
        toast.success("Xóa sản phẩm thành công!");
        setSelectedProducts([]); // Xóa danh sách sản phẩm đã chọn
        fetchProducts();
        fetchAllProducts();
      } else {
        toast.error("Xóa sản phẩm thất bại!");
      }
    } catch (error) {
      console.error("Error deleting selected products:", error);
      toast.error("Có lỗi xảy ra khi xóa sản phẩm!");
      setLoading(false);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-2">
      <div className="flex flex-col gap-4">
        <h1 className="font-bold text-lg">Quản lý sản phẩm</h1>
        <div className="flex gap-2">
          <ButtonComponent
            label={"Thêm sản phẩm"}
            prefix={<IconAdd className="w-4 h-4 fill-white" />}
            onClick={() => showModal()}
          />
          {/* Modal thêm sản phẩm */}
          <Modal
            title={editingProduct ? "Chi tiết sản phẩm" : "Thêm sản phẩm"}
            open={isModalVisible}
            onCancel={handleCancel}
            footer={null}
            width={800}
            destroyOnClose={true}
          >
            <AddProductForm
              onAddProduct={handleAddProduct}
              onEditProduct={handleEditProduct}
              loading={loading}
              initialValues={editingProduct}
              allProductList={allProductList}
            />
          </Modal>
          <ButtonComponent
            label={"Tải sản phẩm"}
            prefix={<IconUpload className="w-4 h-4 fill-white" />}
            className={"!bg-green-500"}
            onClick={() => showImportModal()}
          />

          {/* Modal Import sản phẩm */}
          <Modal
            title="Tải sản phẩm"
            open={isImportModalVisible}
            onCancel={handleImportCancel}
            footer={null}
            width={600}
          >
            <ImportProductsForm
              onImport={handleImportProducts}
              loading={loading}
            />
          </Modal>
          <button
            className="ring-1 ring-red-500 rounded-lg py-1 px-4 text-red-500"
            disabled={selectedProducts.length === 0}
            onClick={handleDeleteSelectedProducts}
          >
            Xoá mục đã chọn
          </button>
          <div className="flex-1">
            <SearchComponent
              onChange={handleSearchChange}
              isSearchOnEnter={false}
            />
          </div>
          <Select
            options={[
              { value: "", label: "Tất cả" },
              { value: "iPhone", label: "iPhone" },
              { value: "iPad", label: "iPad" },
              { value: "watch", label: "Watch" },
              { value: "airpods", label: "AirPods" },
              { value: "airtag", label: "AirTag" },
              { value: "accessories", label: "Phụ kiện" },
            ]}
            onChange={handleCategoryChange}
            placeholder="Chọn loại sản phẩm"
          />
          <Select
            options={[
              { value: "", label: "Mặc định" },
              { value: "asc", label: "A-Z" },
              { value: "desc", label: "Z-A" },
            ]}
            onChange={handleOrderChange}
            placeholder="Sắp xếp"
          />
        </div>
      </div>
      <Spin spinning={loading}>
        <ProductTable
          products={products}
          columns={columns}
          onPageChange={handlePageChange}
          totalRecord={totalRecord}
          currentPage={currentPage}
          onPageSizeChange={handlePageSizeChange}
          pageSize={pageSize}
          refetchData={fetchProducts}
          setIsModalVisible={setIsModalVisible}
          setEditingProduct={setEditingProduct}
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
        />
      </Spin>
    </div>
  );
};

export default ProductList;
